import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { RoiDto, RoiGeometry } from "@gazelab/shared";
import { overlayToCanvas, type OverlaySpec } from "../../lib/heatmapCanvas.js";
import { rectFromDrag } from "../../lib/roiDraw.js";

/**
 * Reproductor del video de estímulo con el mapa de calor de la ventana actual superpuesto. El
 * contenedor tiene el aspecto del video, así que el lienzo coincide con el área pintada y las
 * coordenadas normalizadas de la mirada se dibujan sin bandas. Informa el tiempo en cada
 * fotograma presentado (requestVideoFrameCallback) y permite dibujar ROIs con el video en pausa.
 */
export function VideoHeatmapPlayer({
  src,
  naturalWidth,
  naturalHeight,
  overlay,
  rois,
  highlightRoiId,
  drawMode,
  onRoiDrawn,
  onTime,
  videoRef,
  maxHeight,
}: {
  src: string;
  naturalWidth: number;
  naturalHeight: number;
  overlay: OverlaySpec | null;
  /** ROIs activos en el tiempo actual. */
  rois: RoiDto[];
  highlightRoiId?: string | null;
  drawMode: boolean;
  onRoiDrawn: (g: RoiGeometry) => void;
  onTime: (ms: number) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  maxHeight: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const [draft, setDraft] = useState<RoiGeometry | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const aspect = naturalHeight / naturalWidth;
  const w = Math.max(0, Math.min(width, maxHeight / aspect));
  const h = w * aspect;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Tiempo del fotograma visible (también en pausa y al saltar).
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let handle = 0;
    const onFrame = (_now: number, meta: VideoFrameCallbackMetadata) => {
      onTimeRef.current(meta.mediaTime * 1000);
      handle = v.requestVideoFrameCallback(onFrame);
    };
    handle = v.requestVideoFrameCallback(onFrame);
    const onSeeked = () => onTimeRef.current(v.currentTime * 1000);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("timeupdate", onSeeked);
    return () => {
      v.cancelVideoFrameCallback(handle);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("timeupdate", onSeeked);
    };
  }, [src, videoRef]);

  const overlayCanvas = useMemo(() => (overlay ? overlayToCanvas(overlay) : null), [overlay]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || w <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
    const ctx = c.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (overlayCanvas) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(overlayCanvas, 0, 0, w, h);
    }
    const drawRect = (g: RoiGeometry, color: string, label?: string, dashed = false) => {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.strokeStyle = color;
      if (dashed) ctx.setLineDash([6, 4]);
      ctx.strokeRect(g.x * w, g.y * h, g.width * w, g.height * h);
      if (label) {
        ctx.font = "600 12px system-ui, sans-serif";
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.fillRect(g.x * w, g.y * h, tw + 6, 16);
        ctx.fillStyle = "#0f172a";
        ctx.fillText(label, g.x * w + 3, g.y * h + 12);
      }
      ctx.restore();
    };
    for (const r of rois) drawRect(r.geometry, r.id === highlightRoiId ? "#facc15" : "#ffffff", r.label);
    if (draft) drawRect(draft, "#facc15", undefined, true);
  }, [w, h, overlayCanvas, rois, highlightRoiId, draft]);

  const local = (e: ReactPointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  return (
    <div ref={wrapRef} className="w-full">
      <div className="relative mx-auto overflow-hidden rounded-lg bg-black ring-1 ring-slate-300" style={{ width: w, height: h }}>
        <video ref={videoRef} src={src} controls={!drawMode} preload="auto" playsInline muted className="absolute inset-0 h-full w-full" data-testid="results-video" />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full"
          style={{ pointerEvents: drawMode ? "auto" : "none", cursor: drawMode ? "crosshair" : undefined, touchAction: "none" }}
          aria-hidden
          onPointerDown={(e) => {
            if (!drawMode || e.button !== 0) return;
            videoRef.current?.pause();
            (e.target as Element).setPointerCapture(e.pointerId);
            drag.current = local(e);
          }}
          onPointerMove={(e) => drag.current && setDraft(rectFromDrag(drag.current, local(e)))}
          onPointerUp={(e) => {
            const start = drag.current;
            drag.current = null;
            setDraft(null);
            if (!start) return;
            const g = rectFromDrag(start, local(e));
            if (g) onRoiDrawn(g);
          }}
          onPointerCancel={() => {
            drag.current = null;
            setDraft(null);
          }}
        />
      </div>
    </div>
  );
}
